import { useMutation, useQueryClient } from '@tanstack/react-query';
import GeneralDialog from '../../shared/general-dialog';
import { useState } from 'react';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import AvatarChip from '../../shared/avatar-chip';

import {
  getUser,
  indexTrialUsers,
  saveTrialUsers,
  UserKeys,
} from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { TrialUserModel } from '../../../types';
import { Messages } from '../../utils/message-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface AddTrialUserDialogProps {
  open: boolean;
  handleClose: () => void;
  trialUsers: string[];
}

export default function AddTrialUserDialog({
  open,
  handleClose,
  trialUsers,
}: AddTrialUserDialogProps) {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [addedUsers, setAddedUsers] = useState<string[]>([]);
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState<string>('');

  const [indexingUsers, setIndexingUsers] = useState(false);

  const maxUsersAllowed = 5;
  // Dynamically calculate if user limit is reached
  const hasReachedUserLimit =
    addedUsers.length + trialUsers.length >= maxUsersAllowed;

  async function checkIfUserNameExists(userEmail: string) {
    try {
      setIsValidating(true);
      await getUser(userEmail);
      return true;
    } catch {
      return false;
    } finally {
      setIsValidating(false);
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: TrialUserModel[]) => saveTrialUsers(data!),
    onSuccess: async () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Added('Trial users'),
      });

      setIndexingUsers(true);
      try {
        await indexTrialUsers();
      } catch (error) {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Adding('trial users'),
        });
      }
      setIndexingUsers(false);
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserList] });
      queryClient.invalidateQueries({ queryKey: [UserKeys.TrialUserEmails] });
      setAddedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding('trial users'),
      });
    },
    onSettled: () => {
      createEvent(analytics.trial_period.add_trial_user, {
        module: ModuleList.modules.trialPeriod.name,
        section: ModuleList.modules.trialPeriod.sections.trialPeriod,
        totalUsers: addedUsers.length + trialUsers.length,
      });
    },
  });

  const handleAddUser = async () => {
    if (!selectedUser) {
      setError(true);
      setHelperText('Please enter a valid email!');
      return;
    }

    const userExists = await checkIfUserNameExists(selectedUser);
    if (userExists) {
      setAddedUsers((prev) => [...prev, selectedUser]);
      setSelectedUser('');
      setError(false);
      setHelperText('');
    } else {
      setError(true);
      setHelperText('User does not exist!');
    }
  };

  const handleDeleteUser = (userEmail: string) => {
    setAddedUsers((prev) => {
      const updatedUsers = prev.filter((user) => user !== userEmail);
      // Clear error and helper text if we drop below the maximum user limit
      if (updatedUsers.length + trialUsers.length < maxUsersAllowed) {
        setError(false);
        setHelperText('');
      }
      return updatedUsers;
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser(event.target.value);
    setError(false); // Clear error state when typing
    setHelperText('');
  };

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title="Choose Users"
      dialogContent={
        <Stack sx={{ px: 4 }} gap={2} height={320}>
          <Typography>
            {`Enter the email of users you would like to add to your trial (maximum of 5).`}
          </Typography>
          <Stack gap={1} sx={{ mt: 2, ml: 2 }}>
            <Stack flexDirection="row" gap={1}>
              <Box sx={{ width: 400 }}>
                <TextField
                  value={selectedUser}
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                  label="Email"
                  sx={{ minWidth: 400 }}
                  disabled={hasReachedUserLimit}
                  error={error}
                  helperText={
                    hasReachedUserLimit
                      ? 'You are only allowed 5 users.'
                      : helperText
                  }
                  InputProps={{
                    endAdornment: isValidating ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              </Box>

              <Button
                sx={{ height: 40 }}
                variant="contained"
                onClick={handleAddUser}
                disabled={!selectedUser || isValidating}
              >
                Add
              </Button>
            </Stack>
            <Stack
              flexDirection="row"
              gap={1}
              flexWrap="wrap"
              sx={{ mt: 1, mb: 2 }}
            >
              {addedUsers.map((user) => (
                <AvatarChip
                  key={user}
                  label={user}
                  type="User"
                  email={user}
                  onDelete={() => handleDeleteUser(user)}
                />
              ))}
              {trialUsers && trialUsers.length > 0
                ? trialUsers.map((user) => {
                    return (
                      <AvatarChip
                        key={user}
                        label={user}
                        type={'User'}
                        email={user}
                      />
                    );
                  })
                : null}
            </Stack>
          </Stack>
        </Stack>
      }
      primaryButton={{
        title: 'Start Using gPanel',
        action: () =>
          mutate(
            addedUsers.map(
              (userEmail) =>
                ({
                  userEmail,
                }) as TrialUserModel
            )
          ),
        loading: isPending || indexingUsers,
        disabled: isPending || indexingUsers,
      }}
    />
  );
}
